import React, { useEffect, useState, useRef } from 'react';
import { 
  Mic, Square, X, Volume2, RotateCcw, Play, Pause, 
  CheckCircle2, AlertCircle, Headphones, 
  Gauge, RefreshCw
} from 'lucide-react';
import type { ExampleSentence, PracticeState } from '../types';
import { compareTextsV2, getDetailedWordDiff, DetailedWordDiff } from '../utils/textComparison';
import { useSpeechSynthesis } from '../hooks/useSpeechSynthesis';
import { translateText } from '../hooks/useTranslation';
import { WordPopover } from './WordPopover';
import confetti from 'canvas-confetti';
import { 
  InMemoryAudioRecorder, 
  decodeAudioBlob, 
  segmentAudioByWords, 
  playBufferSegment, 
  WordAudioSegment 
} from '../utils/audioSegmentation';
import { getBrazilianPhoneticGuide, BrazilianPhoneticGuide } from '../utils/brazilianPhonetics';
import { createPortal } from 'react-dom';

interface PracticeModalProps {
  example: ExampleSentence;
  practiceState: PracticeState;
  onClose: () => void;
  onStartRecording: () => void;
  onStopRecording: () => void;
  isRecording: boolean;
  isStarting?: boolean;
  activity?: boolean;
  setScoreActivity?: (score: number) => void;
  disableTryAgain?: boolean;
  isWordOnly?: boolean;
}

export function PracticeModal({
  example,
  practiceState,
  onClose,
  onStartRecording,
  onStopRecording,
  isRecording,
  isStarting = false,
  activity,
  setScoreActivity,
  disableTryAgain,
  isWordOnly = false
}: PracticeModalProps) {
  const { speak, cancel } = useSpeechSynthesis({ lang: 'en-US' });
  const [selectedWord, setSelectedWord] = useState<string | null>(null);
  const [comparisonResults, setComparisonResults] = useState<Array<{
    original: string;
    spoken: string;
    isMatch: boolean;
    similarity: number;
    status: 'correct' | 'close' | 'incorrect';
  }> | null>(null);
  const [score, setScore] = useState<number | null>(null);
  const [translatedText, setTranslatedText] = useState<string | null>(null);
  const lastConfettiTranscription = useRef<string | null>(null);

  // In-memory audio recording & comparison states
  const audioRecorderRef = useRef<InMemoryAudioRecorder>(new InMemoryAudioRecorder());
  const [userAudioBlob, setUserAudioBlob] = useState<Blob | null>(null);
  const [userAudioUrl, setUserAudioUrl] = useState<string | null>(null);
  const [audioBuffer, setAudioBuffer] = useState<AudioBuffer | null>(null);
  const [wordSegments, setWordSegments] = useState<WordAudioSegment[]>([]);
  const [selectedWordIndex, setSelectedWordIndex] = useState<number | null>(null);

  // Playback control states
  const [isPlayingSystem, setIsPlayingSystem] = useState(false);
  const [isPlayingUser, setIsPlayingUser] = useState(false);
  const [isComparing, setIsComparing] = useState(false);
  const isComparingRef = useRef(false);
  const [comparisonStep, setComparisonStep] = useState<'none' | 'system' | 'user'>('none');
  const [systemSpeed, setSystemSpeed] = useState<1.0 | 0.75>(1.0);
  const [accent, setAccent] = useState<'en-US' | 'en-GB'>('en-US');
  const currentStopFnRef = useRef<(() => void) | null>(null);
  const wasRecordingRef = useRef<boolean>(false);
  const userAudioPlayerRef = useRef<HTMLAudioElement | null>(null);
  const comparisonTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Helper function to remove quotes from text while preserving intra-word apostrophes
  const removeQuotes = (text: string) => {
    return text.replace(/["“”]/g, '').replace(/(^|\s)['‘]|['’](\s|$)/g, '$1$2');
  };

  const getCleanExampleText = () => {
    if (!example?.text) return '';
    const cleanText = example.text
      .replace(/{\/?it}|\/it\//g, '')
      .replace(/{\/?phrase}/g, '')
      .replace(/―/g, '');
    return removeQuotes(cleanText).trim();
  };

  // Synchronize In-Memory Audio Recording with isRecording state
  useEffect(() => {
    if (isRecording && !wasRecordingRef.current) {
      wasRecordingRef.current = true;
      // Start capturing audio strictly in memory
      audioRecorderRef.current.start().catch((err) => {
        console.warn('Could not start in-memory MediaRecorder:', err);
      });
    } else if (!isRecording && wasRecordingRef.current) {
      wasRecordingRef.current = false;
      // Stop and obtain in-memory audio Blob
      audioRecorderRef.current.stop().then(async (blob) => {
        if (blob && blob.size > 0) {
          setUserAudioBlob(blob);
          if (userAudioUrl && typeof URL.revokeObjectURL === 'function') {
            URL.revokeObjectURL(userAudioUrl);
          }
          const nextUrl = URL.createObjectURL(blob);
          setUserAudioUrl(nextUrl);

          try {
            const decoded = await decodeAudioBlob(blob);
            setAudioBuffer(decoded);

            // Compute word segments
            const cleanText = getCleanExampleText();
            const words = cleanText.split(/\s+/).filter(Boolean);
            const segments = segmentAudioByWords(decoded, words);
            setWordSegments(segments);
          } catch (e) {
            console.warn('Failed to decode recorded audio buffer:', e);
          }
        }
      });
    }
  }, [isRecording]);

  // Clean up object URLs and audio hardware on unmount
  useEffect(() => {
    return () => {
      audioRecorderRef.current.dispose();
      if (userAudioUrl && typeof URL.revokeObjectURL === 'function') {
        URL.revokeObjectURL(userAudioUrl);
      }
      if (currentStopFnRef.current) {
        currentStopFnRef.current();
      }
      if (comparisonTimeoutRef.current) {
        clearTimeout(comparisonTimeoutRef.current);
      }
      cancel();
    };
  }, [userAudioUrl, cancel]);

  const stopAllPlayback = (stopSpeech = true) => {
    if (stopSpeech) {
      cancel();
    }
    if (comparisonTimeoutRef.current) {
      clearTimeout(comparisonTimeoutRef.current);
      comparisonTimeoutRef.current = null;
    }
    if (currentStopFnRef.current) {
      currentStopFnRef.current();
      currentStopFnRef.current = null;
    }
    if (userAudioPlayerRef.current) {
      userAudioPlayerRef.current.pause();
      userAudioPlayerRef.current.currentTime = 0;
    }
    setIsPlayingSystem(false);
    setIsPlayingUser(false);
    isComparingRef.current = false;
    setIsComparing(false);
    setComparisonStep('none');
  };

  const handlePlayExample = (speed: number = systemSpeed, customText?: string, overrideAccent?: 'en-US' | 'en-GB') => {
    stopAllPlayback(false);
    const textToSpeak = customText || getCleanExampleText();
    if (!textToSpeak) return;
    const targetAccent = overrideAccent || accent;
    if (overrideAccent) {
      setAccent(overrideAccent);
    }

    try {
      setIsPlayingSystem(true);
      speak(textToSpeak, targetAccent, speed, () => {
        setIsPlayingSystem(false);
      });
    } catch (error) {
      console.error('[PracticeModal] Error playing speech:', error);
      setIsPlayingSystem(false);
    }
  };

  const handlePlayUserAudio = () => {
    stopAllPlayback();
    if (!userAudioUrl) return;

    if (userAudioPlayerRef.current) {
      setIsPlayingUser(true);
      userAudioPlayerRef.current.currentTime = 0;
      userAudioPlayerRef.current.play().catch(() => setIsPlayingUser(false));
    }
  };

  // 1-Click Comparative Audio Playback: System Audio -> crisp natural pause -> User Voice
  const handleCompareAudio = () => {
    stopAllPlayback();
    const textToSpeak = getCleanExampleText();
    if (!textToSpeak || !userAudioUrl) return;

    isComparingRef.current = true;
    setIsComparing(true);
    setComparisonStep('system');

    let hasTransitioned = false;
    const playUserVoiceNow = () => {
      if (hasTransitioned || !isComparingRef.current) return;
      hasTransitioned = true;
      if (comparisonTimeoutRef.current) {
        clearTimeout(comparisonTimeoutRef.current);
        comparisonTimeoutRef.current = null;
      }
      setComparisonStep('user');
      setIsPlayingUser(true);
      if (userAudioPlayerRef.current) {
        userAudioPlayerRef.current.currentTime = 0;
        userAudioPlayerRef.current.play().catch(() => {
          isComparingRef.current = false;
          setIsComparing(false);
          setComparisonStep('none');
          setIsPlayingUser(false);
        });
      }
    };

    // System speaks example sentence; on speech end, transition quickly to user voice (120ms gap)
    speak(textToSpeak, accent, systemSpeed, () => {
      if (comparisonTimeoutRef.current) {
        clearTimeout(comparisonTimeoutRef.current);
      }
      comparisonTimeoutRef.current = setTimeout(() => {
        playUserVoiceNow();
      }, 120);
    });

    // Fallback safety timeout in case browser SpeechSynthesis onend fails to fire
    const wordCount = textToSpeak.split(/\s+/).length;
    const safetyDurationMs = Math.max(1200, ((wordCount * 350) / systemSpeed) + 800);
    comparisonTimeoutRef.current = setTimeout(() => {
      playUserVoiceNow();
    }, safetyDurationMs);
  };

  // Play isolated audio segment of user saying a specific word
  const handlePlayWordSegment = (segmentIndex: number) => {
    stopAllPlayback();
    const segment = wordSegments[segmentIndex];
    if (!audioBuffer || !segment) {
      // Fallback to playing full audio
      handlePlayUserAudio();
      return;
    }

    setIsPlayingUser(true);
    currentStopFnRef.current = playBufferSegment(
      audioBuffer,
      segment.startTime,
      segment.duration,
      1.0,
      () => setIsPlayingUser(false)
    );
  };

  // Compare single word: System speaks word -> crisp 100ms pause -> User slice plays
  const handleCompareWord = (word: string, segmentIndex: number) => {
    stopAllPlayback();
    isComparingRef.current = true;
    setIsComparing(true);
    setComparisonStep('system');

    let hasTransitioned = false;
    const playWordSliceNow = () => {
      if (hasTransitioned || !isComparingRef.current) return;
      hasTransitioned = true;
      if (comparisonTimeoutRef.current) {
        clearTimeout(comparisonTimeoutRef.current);
        comparisonTimeoutRef.current = null;
      }
      setComparisonStep('user');
      handlePlayWordSegment(segmentIndex);

      const segment = wordSegments[segmentIndex];
      const segmentDurationMs = segment ? Math.round(segment.duration * 1000) + 150 : 800;
      comparisonTimeoutRef.current = setTimeout(() => {
        setIsComparing(false);
        setComparisonStep('none');
      }, segmentDurationMs);
    };

    speak(word, accent, 1.0, () => {
      if (comparisonTimeoutRef.current) {
        clearTimeout(comparisonTimeoutRef.current);
      }
      comparisonTimeoutRef.current = setTimeout(() => {
        playWordSliceNow();
      }, 100);
    });

    // Fallback safety timeout for single word
    comparisonTimeoutRef.current = setTimeout(() => {
      playWordSliceNow();
    }, 900);
  };

  const handleWordClick = (word: string, index: number, event?: React.MouseEvent) => {
    if (event) {
      event.stopPropagation();
    }
    if (selectedWordIndex === index) {
      setSelectedWordIndex(null);
      setSelectedWord(null);
      return;
    }

    setSelectedWordIndex(index);

    const cleaned = word
      .replace(/'s\b/g, ' is')
      .replace(/'re\b/g, ' are')
      .replace(/'ve\b/g, ' have')
      .replace(/'d\b/g, ' would')
      .replace(/'ll\b/g, ' will')
      .replace(/'m\b/g, ' am')
      .replace(/―/g, '')
      .replace(/n't\b/g, ' not')
      .replace(/{\/?it}|\/it\//g, '')
      .replace(/{\/?phrase}/g, '')
      .replace(/\bok\b/g, 'okay')
      .replace(/\bOK\b/g, 'okay')
      .replace(/\bcan't\b/g, 'cannot')
      .replace(/[=[\];….]/g, '')
      .replace(/\bwon't\b/g, 'will not')
      .trim()
      .split(' ')[0];

    setSelectedWord(cleaned);
  };

  const getWordBadgeStyle = (status: string, isSelected: boolean) => {
    const base = 'inline-flex items-center px-2 py-1 sm:px-2.5 sm:py-1 rounded-lg text-xs sm:text-sm font-semibold transition-all cursor-pointer m-0.5 sm:m-1 break-words max-w-full ';
    const selectedRing = isSelected ? 'ring-2 ring-purple-600 ring-offset-1 shadow-md scale-105 ' : 'hover:scale-102 ';
    switch (status) {
      case 'correct':
        return base + selectedRing + 'bg-green-100 text-green-800 border border-green-300';
      case 'close':
        return base + selectedRing + 'bg-amber-100 text-amber-800 border border-amber-300';
      case 'incorrect':
        return base + selectedRing + 'bg-red-100 text-red-800 border border-red-300';
      default:
        return base + selectedRing + 'bg-purple-100 text-purple-800 border border-purple-200';
    }
  };

  const closeModal = () => {
    stopAllPlayback();
    setSelectedWord(null);
    setSelectedWordIndex(null);
    onClose();
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeModal();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  useEffect(() => {
    if (practiceState && practiceState.transcription && example && example.text) {
      const cleanedExample = removeQuotes(example.text
        .replace(/'re\b/g, ' are')
        .replace(/'ve\b/g, ' have')
        .replace(/'d\b/g, ' would')
        .replace(/'ll\b/g, ' will')
        .replace(/{\/?it}|\/it\//g, '')
        .replace(/'m\b/g, ' am')
        .replace(/―/g, '')
        .replace(/,/g, '')
        .replace(/\//g, ' ')
        .replace(/n't\b/g, ' not')
        .replace(/{\/?phrase}/g, '')
        .replace(/\bok\b/g, 'okay')
        .replace(/\bOK\b/g, 'okay')
        .replace(/\bcan't\b/g, 'cannot')
        .replace(/[=[\];….]/g, '')
        .replace(/\.\.\./g, '')
        .replace(/\bwon't\b/g, 'will not')
        .trim());

      const result = practiceState.transcription
        ? compareTextsV2(cleanedExample, practiceState.transcription)
        : null;

      setComparisonResults(result as Array<{
        original: string;
        spoken: string;
        isMatch: boolean;
        similarity: number;
        status: 'correct' | 'close' | 'incorrect';
      }> | null);
      
      if (result) {
        const total = result.length;
        const correct = result.filter((r) => r.status === 'correct').length;
        const close = result.filter((r) => r.status === 'close').length;
        const calculatedScore = Math.round(((correct + close * 0.5) / total) * 100);
        setScore(calculatedScore);
        
        if (calculatedScore === 100 && practiceState.transcription !== lastConfettiTranscription.current) {
          confetti({
            particleCount: 100,
            spread: 70,
            origin: { y: 0.6 }
          });
          lastConfettiTranscription.current = practiceState.transcription;
        }

        if (setScoreActivity) {
          setScoreActivity(calculatedScore);
        }
      } else {
        setScore(null);
        if (setScoreActivity) {
          setScoreActivity(0);
        }
      }
    }
  }, [practiceState, example, setScoreActivity]);

  useEffect(() => {
    let isCancelled = false;
    if (example && example.text) {
      const cleanedText = removeQuotes(example.text
        .replace(/[.,!?%-]/g, ' ')
        .replace(/{\/?it}|\/it\//g, '')
        .replace(/{\/?phrase}/g, '')
        .replace(/\bok\b/g, 'okay')
        .replace(/\bOK\b/g, 'okay')
        .replace(/["()=[\];…]/g, '')
        .replace(/\.\.\./g, ''));
      translateText(cleanedText, 'en', 'pt').then((translation) => {
        if (!isCancelled) {
          setTranslatedText(translation);
        }
      }).catch(() => {});
    }
    return () => {
      isCancelled = true;
    };
  }, [example?.text]);

  const wordComparisonResults = comparisonResults
    ? comparisonResults.filter((r) => r.original !== '')
    : null;

  interface AlignedWordToken {
    displayWord: string;
    cleanWord: string;
    status: 'correct' | 'close' | 'incorrect';
    compIndex: number;
    spoken: string;
  }

  const alignOriginalTokensWithResults = (
    originalText: string,
    results: Array<{ original: string; spoken: string; isMatch: boolean; similarity: number; status: 'correct' | 'close' | 'incorrect' }>
  ): AlignedWordToken[] => {
    const originalTokens = originalText.split(/\s+/).filter(Boolean);
    let compIdx = 0;

    return originalTokens.map((token) => {
      const cleanWord = token.toLowerCase().replace(/[.,#!$%^&*;:{}=_`~()?"'[\]<>…]/g, '');
      if (compIdx >= results.length) {
        return {
          displayWord: token,
          cleanWord: cleanWord || token,
          status: 'correct',
          compIndex: compIdx,
          spoken: ''
        };
      }

      const currentComp = results[compIdx];
      let status = currentComp.status;
      let spoken = currentComp.spoken;
      const matchedCompIdx = compIdx;
      compIdx++;

      // Check if subsequent comparison tokens were split from a single original contraction/hyphenated token
      if (compIdx < results.length) {
        const nextComp = results[compIdx];
        const tokenLower = token.toLowerCase();
        if (
          nextComp.original &&
          (cleanWord.includes(nextComp.original) ||
            tokenLower.includes(nextComp.original) ||
            (cleanWord === 'didnt' && nextComp.original === 'not') ||
            (cleanWord === 'dont' && nextComp.original === 'not') ||
            (cleanWord === 'wont' && nextComp.original === 'not') ||
            (cleanWord === 'cant' && nextComp.original === 'not') ||
            (cleanWord === 'isnt' && nextComp.original === 'not') ||
            (cleanWord === 'arent' && nextComp.original === 'not') ||
            (cleanWord === 'wasnt' && nextComp.original === 'not') ||
            (cleanWord === 'werent' && nextComp.original === 'not') ||
            (cleanWord === 'couldnt' && nextComp.original === 'not') ||
            (cleanWord === 'shouldnt' && nextComp.original === 'not') ||
            (cleanWord === 'wouldnt' && nextComp.original === 'not') ||
            (cleanWord === 'hasnt' && nextComp.original === 'not') ||
            (cleanWord === 'havent' && nextComp.original === 'not') ||
            (cleanWord === 'hadnt' && nextComp.original === 'not'))
        ) {
          if (nextComp.status === 'incorrect' || status === 'incorrect') {
            status = 'incorrect';
          } else if (nextComp.status === 'close' || status === 'close') {
            status = 'close';
          }
          spoken = `${spoken} ${nextComp.spoken}`.trim();
          compIdx++;
        }
      }

      return {
        displayWord: token,
        cleanWord: cleanWord || token,
        status,
        compIndex: matchedCompIdx,
        spoken
      };
    });
  };

  const alignedTokens = wordComparisonResults
    ? alignOriginalTokensWithResults(getCleanExampleText(), wordComparisonResults)
    : null;

  const content = (
    <div 
      className={`${
        activity 
          ? 'w-full my-2' 
          : 'z-[100] fixed inset-0 bg-black/60 backdrop-blur-xs overflow-y-auto overscroll-contain p-2 sm:p-4 flex items-center justify-center'
      }`} 
      onClick={!activity ? closeModal : undefined}
    >
      <div
        className={`bg-white rounded-2xl w-full max-w-2xl shadow-2xl flex flex-col ${
          activity 
            ? 'p-2 sm:p-4 border border-gray-100' 
            : 'max-h-[92dvh] sm:max-h-[90vh] overflow-hidden overscroll-contain'
        }`}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Hidden Audio Tag for in-memory user playback */}
        {userAudioUrl && (
          <audio
            ref={userAudioPlayerRef}
            src={userAudioUrl}
            preload="auto"
            onEnded={() => {
              setIsPlayingUser(false);
              isComparingRef.current = false;
              setIsComparing(false);
              setComparisonStep('none');
            }}
          />
        )}

        {/* Modal Header */}
        {!activity && (
          <div className="shrink-0 flex justify-between items-center px-4 pt-3.5 pb-3 sm:px-6 sm:pt-5 sm:pb-3.5 border-b border-gray-100 bg-white">
            <div className="flex items-center gap-2 min-w-0">
              <Headphones className="w-5 h-5 text-purple-600 shrink-0" />
              <h3 className="text-lg sm:text-xl font-bold text-gray-900 truncate">Treino de Pronúncia</h3>
            </div>
            <button
              onClick={closeModal}
              className="shrink-0 p-1.5 sm:p-2 hover:bg-gray-100 text-gray-500 rounded-lg transition-colors"
              aria-label="Fechar modal"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        )}

        {/* Main Content Area */}
        <div
          className={`space-y-3.5 sm:space-y-5 flex-1 min-h-0 ${
            !activity ? 'overflow-y-auto overscroll-contain px-3.5 py-3.5 sm:px-6 sm:py-5' : 'p-1'
          }`}
        >
          {/* NO SPEECH DETECTED WARNING BANNER */}
          {practiceState?.attempts > 0 && !isRecording && !practiceState?.transcription && (
            <div
              data-testid="practice-no-speech-warning"
              className="bg-amber-50 border border-amber-200 rounded-xl sm:rounded-2xl p-3.5 sm:p-4 text-amber-900 shadow-2xs flex items-start gap-3"
            >
              <AlertCircle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
              <div className="min-w-0">
                <div className="text-xs sm:text-sm font-bold text-amber-900">
                  Nenhuma fala detectada pelo microfone
                </div>
                <p className="text-xs text-amber-800 mt-1">
                  Não conseguimos ouvir sua voz com clareza. Fale mais perto do microfone e toque em <strong>Try Again</strong> para tentar novamente.
                </p>
              </div>
            </div>
          )}

          {/* PROMINENT SCORE BANNER (visible immediately on mobile after speaking) */}
          {score !== null && practiceState.transcription && !isRecording && (
            <div
              data-testid="practice-score-banner"
              className="bg-gradient-to-r from-purple-700 via-indigo-600 to-purple-800 rounded-xl sm:rounded-2xl p-3.5 sm:p-4 text-white shadow-md flex items-center justify-between gap-3 animate-in fade-in duration-300"
            >
              <div className="flex items-center gap-3 min-w-0">
                <div
                  className={`w-12 h-12 sm:w-14 sm:h-14 rounded-xl flex items-center justify-center font-black text-base sm:text-lg shadow-inner shrink-0 ${
                    score >= 90
                      ? 'bg-emerald-500 text-white ring-2 ring-emerald-300'
                      : score >= 70
                      ? 'bg-amber-400 text-gray-900 ring-2 ring-amber-200'
                      : 'bg-rose-500 text-white ring-2 ring-rose-300'
                  }`}
                >
                  {score}%
                </div>
                <div className="min-w-0">
                  <div className="text-[11px] sm:text-xs uppercase font-bold tracking-wider text-purple-200">
                    Sua Pontuação
                  </div>
                  <div className="text-sm sm:text-base font-extrabold truncate">
                    {score === 100
                      ? '🌟 Pronúncia Perfeita!'
                      : score >= 90
                      ? '🎉 Excelente Pronúncia!'
                      : score >= 70
                      ? '👍 Muito Bom, quase lá!'
                      : '💪 Continue Praticando!'}
                  </div>
                  <div className="text-xs text-purple-200 truncate">
                    {alignedTokens
                      ? `${alignedTokens.filter((t) => t.status === 'correct').length} de ${alignedTokens.length} palavras corretas`
                      : 'Feedback calculado'}
                  </div>
                </div>
              </div>

              {practiceState?.attempts > 0 && !disableTryAgain && (
                <button
                  type="button"
                  onClick={onStartRecording}
                  className="px-3 py-1.5 bg-white/20 hover:bg-white/30 text-white rounded-lg text-xs font-bold transition-colors flex items-center gap-1.5 shrink-0 border border-white/30"
                  title="Tentar falar novamente"
                >
                  <RotateCcw className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">Repetir</span>
                </button>
              )}
            </div>
          )}

          {/* Target Phrase / Word Card */}
          <div className="bg-gradient-to-br from-purple-50 via-indigo-50/50 to-white rounded-xl sm:rounded-2xl p-3.5 sm:p-5 border border-purple-100 shadow-xs">
            <div className="flex flex-wrap justify-between items-center gap-2 mb-2.5 sm:mb-3">
              <h4 className="text-xs font-bold uppercase tracking-wider text-purple-700">
                {isWordOnly ? 'Palavra para praticar' : 'Frase de exemplo'}
              </h4>
              <div className="flex items-center gap-1.5 flex-wrap">
                {/* Speed Toggle */}
                <button
                  onClick={() => setSystemSpeed(systemSpeed === 1.0 ? 0.75 : 1.0)}
                  className={`px-2 py-1 rounded text-xs font-medium border flex items-center gap-1 transition-colors ${
                    systemSpeed === 0.75 
                      ? 'bg-amber-100 text-amber-800 border-amber-300' 
                      : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                  }`}
                  title="Alterar velocidade da pronúncia do sistema"
                >
                  <Gauge className="w-3 h-3" />
                  <span>{systemSpeed}x</span>
                </button>
                {/* Speak System Example (US & UK Accents) */}
                <button
                  type="button"
                  data-testid="practice-play-us"
                  onClick={() => handlePlayExample(systemSpeed, undefined, 'en-US')}
                  className={`px-2.5 py-1 rounded-lg text-xs font-semibold border transition-colors flex items-center gap-1 ${
                    accent === 'en-US'
                      ? 'bg-purple-600 text-white border-purple-600 hover:bg-purple-700'
                      : 'bg-white text-purple-700 border-purple-200 hover:bg-purple-50'
                  }`}
                  title="Ouvir pronúncia americana (US)"
                >
                  <Volume2 className={`w-3.5 h-3.5 ${isPlayingSystem && accent === 'en-US' ? 'animate-pulse' : ''}`} />
                  <span>🇺🇸 US</span>
                </button>
                <button
                  type="button"
                  data-testid="practice-play-uk"
                  onClick={() => handlePlayExample(systemSpeed, undefined, 'en-GB')}
                  className={`px-2.5 py-1 rounded-lg text-xs font-semibold border transition-colors flex items-center gap-1 ${
                    accent === 'en-GB'
                      ? 'bg-indigo-600 text-white border-indigo-600 hover:bg-indigo-700'
                      : 'bg-white text-indigo-700 border-indigo-200 hover:bg-indigo-50'
                  }`}
                  title="Ouvir pronúncia britânica (UK)"
                >
                  <Volume2 className={`w-3.5 h-3.5 ${isPlayingSystem && accent === 'en-GB' ? 'animate-pulse' : ''}`} />
                  <span>🇬🇧 UK</span>
                </button>
              </div>
            </div>

            {/* Word by Word Display / Interactive Chips */}
            <div className="text-base sm:text-lg leading-relaxed flex flex-wrap items-center gap-1.5 my-2 break-words max-w-full">
              {alignedTokens ? (
                alignedTokens.map((item, idx) => (
                  <span
                    key={idx}
                    className={getWordBadgeStyle(item.status, selectedWordIndex === item.compIndex)}
                    onClick={(e) => handleWordClick(item.cleanWord, item.compIndex, e)}
                    title="Clique para comparar e ver dicas desta palavra"
                  >
                    {item.displayWord}
                  </span>
                ))
              ) : (
                <div className="text-gray-800 font-medium flex flex-wrap items-center gap-1.5 break-words max-w-full">
                  {getCleanExampleText().split(/\s+/).filter(Boolean).map((word, i) => (
                    <span
                      key={i}
                      className={`cursor-pointer hover:text-purple-600 underline mr-1 break-words ${
                        selectedWordIndex === i ? 'text-purple-700 font-bold bg-purple-100 px-1 rounded' : ''
                      }`}
                      onClick={(e) => handleWordClick(word, i, e)}
                    >
                      {word}
                    </span>
                  ))}
                </div>
              )}
            </div>

            {translatedText && (
              <p className="text-xs text-gray-500 italic mt-2 pt-2 border-t border-purple-100">
                Tradução: {translatedText}
              </p>
            )}
          </div>

          {/* WORD RESULTS BREAKDOWN (explicit per-word feedback) */}
          {alignedTokens && !isRecording && (
            <div
              data-testid="word-results-breakdown"
              className="bg-white rounded-xl sm:rounded-2xl p-3 sm:p-4 border border-purple-100 shadow-xs"
            >
              <div className="flex flex-wrap items-center justify-between gap-1.5 mb-2.5">
                <span className="text-xs font-bold uppercase tracking-wider text-gray-800 flex items-center gap-1.5">
                  <CheckCircle2 className="w-4 h-4 text-purple-600 shrink-0" />
                  Resultados por Palavra:
                </span>
                <div className="flex items-center gap-2 text-[11px] text-gray-500">
                  <span className="flex items-center gap-1">
                    <span className="w-2 h-2 rounded-full bg-green-500 inline-block" /> Correto
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="w-2 h-2 rounded-full bg-amber-400 inline-block" /> Quase lá
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="w-2 h-2 rounded-full bg-red-400 inline-block" /> Ajustar
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 sm:gap-2">
                {alignedTokens.map((item, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={(e) => handleWordClick(item.cleanWord, item.compIndex, e)}
                    className={`flex items-center justify-between px-3 py-2 rounded-xl border text-left transition-all ${
                      selectedWordIndex === item.compIndex
                        ? 'ring-2 ring-purple-600 bg-purple-50 border-purple-300 shadow-xs'
                        : item.status === 'correct'
                        ? 'bg-green-50/50 border-green-200 text-green-900 hover:bg-green-100/60'
                        : item.status === 'close'
                        ? 'bg-amber-50/60 border-amber-200 text-amber-900 hover:bg-amber-100/60'
                        : 'bg-red-50/60 border-red-200 text-red-900 hover:bg-red-100/60'
                    }`}
                  >
                    <div className="min-w-0 pr-2">
                      <div className="text-xs sm:text-sm font-bold truncate">{item.displayWord}</div>
                      {item.spoken && item.status !== 'correct' && (
                        <div className="text-[10px] text-gray-500 truncate">Falou: "{item.spoken}"</div>
                      )}
                    </div>
                    <span
                      className={`text-[10px] sm:text-[11px] font-bold px-2 py-0.5 rounded-full shrink-0 ${
                        item.status === 'correct'
                          ? 'bg-green-100 text-green-800'
                          : item.status === 'close'
                          ? 'bg-amber-200 text-amber-900'
                          : 'bg-red-100 text-red-800'
                      }`}
                    >
                      {item.status === 'correct' ? 'Perfeito' : item.status === 'close' ? 'Quase lá' : 'Ajustar'}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* DUAL AUDIO PLAYBACK & SEQUENTIAL COMPARATOR (System vs User Voice) */}
          {userAudioUrl && !isRecording && (
            <div className="bg-slate-50 border border-slate-200 rounded-xl sm:rounded-2xl p-3 sm:p-4 shadow-xs">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1 mb-3">
                <span className="text-xs font-bold text-slate-700 uppercase tracking-wider flex items-center gap-1.5">
                  <Headphones className="w-4 h-4 text-purple-600 shrink-0" />
                  Comparação de Áudio
                </span>
                <span className="text-[11px] text-slate-500">Gravado 100% na memória do navegador</span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 sm:gap-3 mb-3">
                {/* System Player */}
                <button
                  onClick={() => handlePlayExample(systemSpeed)}
                  disabled={isPlayingSystem || isComparing}
                  className={`flex items-center justify-between p-2.5 sm:p-3 rounded-xl border transition-all ${
                    isPlayingSystem || (isComparing && comparisonStep === 'system')
                      ? 'bg-purple-100 border-purple-400 shadow-sm ring-1 ring-purple-400'
                      : 'bg-white border-slate-200 hover:border-purple-300 hover:bg-purple-50/50'
                  }`}
                >
                  <div className="flex items-center gap-2.5 min-w-0">
                    <span className="p-2 rounded-lg bg-purple-600 text-white shrink-0">
                      <Volume2 className="w-4 h-4" />
                    </span>
                    <div className="text-left min-w-0">
                      <div className="text-xs font-bold text-gray-900 truncate">Pronúncia Nativa</div>
                      <div className="text-[11px] text-gray-500">Sistema ({systemSpeed}x)</div>
                    </div>
                  </div>
                  {isPlayingSystem || (isComparing && comparisonStep === 'system') ? (
                    <span className="text-xs font-bold text-purple-700 animate-pulse shrink-0">Tocando...</span>
                  ) : (
                    <Play className="w-4 h-4 text-gray-400 shrink-0" />
                  )}
                </button>

                {/* User Player */}
                <button
                  onClick={handlePlayUserAudio}
                  disabled={isPlayingUser || isComparing}
                  className={`flex items-center justify-between p-2.5 sm:p-3 rounded-xl border transition-all ${
                    isPlayingUser || (isComparing && comparisonStep === 'user')
                      ? 'bg-emerald-100 border-emerald-400 shadow-sm ring-1 ring-emerald-400'
                      : 'bg-white border-slate-200 hover:border-emerald-300 hover:bg-emerald-50/50'
                  }`}
                >
                  <div className="flex items-center gap-2.5 min-w-0">
                    <span className="p-2 rounded-lg bg-emerald-600 text-white shrink-0">
                      <Mic className="w-4 h-4" />
                    </span>
                    <div className="text-left min-w-0">
                      <div className="text-xs font-bold text-gray-900 truncate">Sua Voz</div>
                      <div className="text-[11px] text-gray-500">Áudio gravado</div>
                    </div>
                  </div>
                  {isPlayingUser || (isComparing && comparisonStep === 'user') ? (
                    <span className="text-xs font-bold text-emerald-700 animate-pulse shrink-0">Tocando...</span>
                  ) : (
                    <Play className="w-4 h-4 text-gray-400 shrink-0" />
                  )}
                </button>
              </div>

              {/* 1-Click Auto Compare Button */}
              <button
                onClick={handleCompareAudio}
                disabled={isComparing || isPlayingSystem || isPlayingUser}
                className={`w-full py-2.5 px-3 sm:px-4 rounded-xl font-semibold text-xs transition-all flex items-center justify-center gap-2 text-center ${
                  isComparing
                    ? 'bg-purple-700 text-white shadow-md animate-pulse'
                    : 'bg-purple-600 text-white hover:bg-purple-700 shadow-sm'
                }`}
              >
                <RefreshCw className={`w-3.5 h-3.5 shrink-0 ${isComparing ? 'animate-spin' : ''}`} />
                <span>
                  {isComparing 
                    ? (comparisonStep === 'system' ? '1. Ouvindo o Sistema...' : '2. Ouvindo Sua Voz...')
                    : 'Comparar Pronúncia (Nativo → Sua Voz)'}
                </span>
              </button>
            </div>
          )}

          {/* WORD-BY-WORD INSPECTOR (when a word is tapped) */}
          {selectedWordIndex !== null && wordComparisonResults && wordComparisonResults[selectedWordIndex] && (() => {
            const result = wordComparisonResults[selectedWordIndex];
            const originalWord = result.original;
            const spokenWord = result.spoken;
            const diff: DetailedWordDiff = getDetailedWordDiff(originalWord, spokenWord);
            const brGuide: BrazilianPhoneticGuide = getBrazilianPhoneticGuide(originalWord);

            return (
              <div className="bg-amber-50/70 border border-amber-200 rounded-xl sm:rounded-2xl p-3 sm:p-4 transition-all shadow-sm">
                <div className="flex items-start justify-between gap-2 mb-3 border-b border-amber-200 pb-2">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
                      <span className="text-xs font-bold uppercase tracking-wider text-amber-900">
                        Análise Palavra por Palavra:
                      </span>
                      <span className="text-sm sm:text-base font-extrabold text-gray-900 break-all">{originalWord}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${
                        result.status === 'correct' 
                          ? 'bg-green-100 text-green-800' 
                          : result.status === 'close' 
                          ? 'bg-amber-200 text-amber-900' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {result.status === 'correct' ? 'Perfeito' : result.status === 'close' ? 'Quase lá' : 'Ajustar'}
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setSelectedWordIndex(null);
                      setSelectedWord(null);
                    }}
                    className="text-gray-400 hover:text-gray-600 p-1 shrink-0"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>

                {/* Word Audio Controls */}
                <div className="flex flex-wrap items-center gap-2 mb-3">
                  <button
                    onClick={() => handlePlayExample(systemSpeed, originalWord, 'en-US')}
                    className="flex-1 sm:flex-initial justify-center px-2.5 sm:px-3 py-1.5 bg-white border border-purple-200 text-purple-700 rounded-lg text-xs font-semibold hover:bg-purple-50 flex items-center gap-1.5 shadow-2xs whitespace-nowrap"
                  >
                    <Volume2 className="w-3.5 h-3.5 shrink-0" />
                    Ouvir Nativo (🇺🇸 US)
                  </button>
                  <button
                    onClick={() => handlePlayExample(systemSpeed, originalWord, 'en-GB')}
                    className="flex-1 sm:flex-initial justify-center px-2.5 sm:px-3 py-1.5 bg-white border border-indigo-200 text-indigo-700 rounded-lg text-xs font-semibold hover:bg-indigo-50 flex items-center gap-1.5 shadow-2xs whitespace-nowrap"
                  >
                    <Volume2 className="w-3.5 h-3.5 shrink-0" />
                    Ouvir Britânico (🇬🇧 UK)
                  </button>

                  {audioBuffer && wordSegments[selectedWordIndex] && (
                    <>
                      <button
                        onClick={() => handlePlayWordSegment(selectedWordIndex)}
                        className="flex-1 sm:flex-initial justify-center px-2.5 sm:px-3 py-1.5 bg-white border border-emerald-200 text-emerald-700 rounded-lg text-xs font-semibold hover:bg-emerald-50 flex items-center gap-1.5 shadow-2xs whitespace-nowrap"
                      >
                        <Mic className="w-3.5 h-3.5 shrink-0" />
                        Ouvir Seu Trecho
                      </button>

                      <button
                        onClick={() => handleCompareWord(originalWord, selectedWordIndex)}
                        className="w-full sm:w-auto justify-center px-2.5 sm:px-3 py-1.5 bg-amber-600 text-white rounded-lg text-xs font-semibold hover:bg-amber-700 flex items-center gap-1.5 shadow-2xs whitespace-nowrap"
                      >
                        <RefreshCw className="w-3.5 h-3.5 shrink-0" />
                        Comparar Esta Palavra
                      </button>
                    </>
                  )}
                </div>

                {/* INTRA-WORD CHARACTER DIFFERENCE HIGHLIGHT */}
                {result.status !== 'correct' && (
                  <div className="bg-white rounded-xl p-3 border border-amber-200 mb-3">
                    <div className="text-xs font-bold text-gray-700 mb-1.5">
                      Onde a pronúncia precisa de ajuste:
                    </div>
                    
                    <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 mb-2">
                      <span className="text-xs text-gray-500 sm:w-16 shrink-0">Esperado:</span>
                      <div className="flex flex-wrap items-center gap-0.5">
                        {diff.expectedChars.map((item, idx) => (
                          <span
                            key={idx}
                            className={`px-1.5 py-0.5 rounded text-xs font-mono font-bold ${
                              item.type === 'correct'
                                ? 'bg-green-100 text-green-800'
                                : item.type === 'missing'
                                ? 'bg-amber-200 text-amber-900 line-through'
                                : 'bg-red-200 text-red-900 ring-1 ring-red-400'
                            }`}
                          >
                            {item.char}
                          </span>
                        ))}
                      </div>
                    </div>

                    {diff.spoken && (
                      <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 mb-2">
                        <span className="text-xs text-gray-500 sm:w-16 shrink-0">Você falou:</span>
                        <div className="flex flex-wrap items-center gap-0.5">
                          {diff.spokenChars.map((item, idx) => (
                            <span
                              key={idx}
                              className={`px-1.5 py-0.5 rounded text-xs font-mono font-bold ${
                                item.type === 'correct'
                                  ? 'bg-green-100 text-green-800'
                                  : item.type === 'extra'
                                  ? 'bg-purple-100 text-purple-900 border border-purple-300'
                                  : 'bg-red-100 text-red-800'
                              }`}
                            >
                              {item.char}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    <p className="text-xs text-amber-900 mt-2 font-medium">
                      💡 {diff.explanation}
                    </p>
                    <p className="text-xs text-slate-700 mt-0.5">
                      👉 <strong>Como falar:</strong> {diff.howToFix}
                    </p>
                  </div>
                )}

                {/* BRAZILIAN PHONETIC GUIDE FOR THIS WORD */}
                <div className="bg-white/90 rounded-xl p-3 border border-amber-100">
                  <div className="flex flex-wrap items-center justify-between gap-1.5 mb-2">
                    <span className="text-xs font-bold text-purple-900 uppercase tracking-wider">
                      Guia Fonético para Brasileiros:
                    </span>
                    <span className="text-xs font-mono font-bold text-gray-600 bg-gray-100 px-2 py-0.5 rounded">
                      {brGuide.ipa}
                    </span>
                  </div>

                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    <span className="text-xs text-gray-600">Como falar:</span>
                    <span className="text-xs sm:text-sm font-extrabold text-purple-700 bg-purple-50 px-2.5 py-1 rounded-md border border-purple-200 break-all">
                      "{brGuide.respelling}"
                    </span>
                  </div>

                  {brGuide.traps.length > 0 && (
                    <div className="text-xs text-amber-900 mt-1 space-y-1 border-t border-amber-100 pt-1.5">
                      {brGuide.traps.slice(0, 2).map((trap, idx) => (
                        <div key={idx} className="flex items-start gap-1">
                          <span className="text-amber-600 font-bold">•</span>
                          <span>{trap}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })()}

          {/* INLINE WORD DETAILS (replaces floating WordPopover on top of modal) */}
          {selectedWord && (
            <WordPopover
              word={selectedWord}
              context={example.text}
              inline={true}
              position={{ top: 0, left: 0 }}
              onClose={() => {
                setSelectedWord(null);
                setSelectedWordIndex(null);
              }}
            />
          )}

          {/* TRANSCRIPTION & SUMMARY SCORE SECTION */}
          {practiceState.transcription && !isRecording && (
            <div className="bg-gray-50 rounded-xl p-3 sm:p-4 border border-gray-100">
              <div className="flex flex-wrap justify-between items-center gap-2 mb-2">
                <h4 className="text-xs font-bold uppercase tracking-wider text-gray-600">Sua Transcrição:</h4>
                {score !== null && (
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium text-gray-500">Pontuação:</span>
                    <span
                      className={`text-sm sm:text-base font-extrabold px-2.5 py-0.5 rounded-full ${
                        score >= 90
                          ? 'bg-green-100 text-green-700'
                          : score >= 70
                          ? 'bg-amber-100 text-amber-700'
                          : 'bg-red-100 text-red-700'
                      }`}
                    >
                      {score}%
                    </span>
                  </div>
                )}
              </div>
              <p className="text-sm sm:text-base text-gray-800 font-medium break-words">{practiceState.transcription}</p>
            </div>
          )}
        </div>

        {/* RECORDING / RETRY CONTROLS */}
        <div
          className={`shrink-0 flex flex-wrap justify-center gap-2.5 sm:gap-3 ${
            !activity ? 'px-4 py-3 sm:px-6 sm:py-4 border-t border-gray-100 bg-white' : 'pt-3'
          }`}
        >
          {isStarting && (
            <button
              disabled
              className="w-full sm:w-auto justify-center flex items-center gap-2 px-6 py-3 rounded-xl font-bold text-sm bg-purple-400 text-white shadow-sm cursor-wait"
            >
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
              Iniciando microfone...
            </button>
          )}

          {!isStarting && !isRecording && !(activity && score === 100) && (
            <button
              onClick={score === 100 ? closeModal : onStartRecording}
              disabled={disableTryAgain && score !== 100}
              className={`w-full sm:w-auto justify-center flex items-center gap-2 px-6 py-3 rounded-xl font-bold text-sm transition-all shadow-sm ${
                disableTryAgain && score !== 100
                  ? 'bg-gray-400 text-gray-200 cursor-not-allowed' 
                  : 'bg-purple-600 text-white hover:bg-purple-700 hover:shadow-md'
              }`}
            >
              {score === 100 ? (
                <>
                  <CheckCircle2 className="w-5 h-5" />
                  Perfect
                </>
              ) : practiceState?.attempts > 0 ? (
                <>
                  <RotateCcw className="w-5 h-5" />
                  Try Again
                </>
              ) : (
                <>
                  <Mic className="w-5 h-5" />
                  Start Recording
                </>
              )}
            </button>
          )}
          
          {!isStarting && !isRecording && score === 100 && !disableTryAgain && (
            <button
              onClick={onStartRecording}
              className="w-full sm:w-auto justify-center flex items-center gap-2 px-6 py-3 bg-white text-purple-600 hover:bg-purple-50 border border-purple-600 rounded-xl font-bold text-sm transition-all shadow-sm"
            >
              <RotateCcw className="w-5 h-5" />
              Try Again
            </button>
          )}

          {isRecording && (
            <button
              onClick={onStopRecording}
              className="w-full sm:w-auto justify-center flex items-center gap-2 px-6 py-3 bg-red-600 text-white rounded-xl hover:bg-red-700 font-bold text-sm transition-all shadow-md animate-pulse"
            >
              <Square className="w-5 h-5" />
              Stop Recording
            </button>
          )}
        </div>
      </div>
    </div>
  );

  if (activity) {
    return content;
  }

  return createPortal(content, document.body);
}
